import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Drawer,
    Grid,
    IconButton,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BlockIcon from '@mui/icons-material/Block';
import RestoreIcon from '@mui/icons-material/Restore';
import EditIcon from '@mui/icons-material/Edit';

import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import {
    FunnelStage,
    FUNNEL_STAGES,
    FUNNEL_STAGE_MAP,
    SalesFunnelProspect,
} from '../../salesFunnel.types';
import {
    advanceSalesFunnelProspect,
    getSalesFunnelProspect,
    markProspectLost,
    reactivateProspect,
} from '../../salesFunnel.api';
import StageDetailsForm from './StageDetailsForm';

interface ProspectDetailDrawerProps {
    open: boolean;
    onClose: () => void;
    prospectUuid: string | null;
    orgUuid: string;
    onProspectUpdated: () => void;
}

const ProspectDetailDrawer: React.FC<ProspectDetailDrawerProps> = ({
    open,
    onClose,
    prospectUuid,
    orgUuid,
    onProspectUpdated,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [prospect, setProspect] = useState<SalesFunnelProspect | null>(null);
    const [loading, setLoading] = useState(false);
    const [lostDialogOpen, setLostDialogOpen] = useState(false);
    const [lostReason, setLostReason] = useState('');
    const [editingStage, setEditingStage] = useState<FunnelStage | null>(null);
    const [advanceOnSave, setAdvanceOnSave] = useState(false);
    const [advancing, setAdvancing] = useState(false);

    const loadProspect = useCallback(async () => {
        if (!prospectUuid || !orgUuid) return;
        setLoading(true);
        try {
            const response = await getSalesFunnelProspect(orgUuid, prospectUuid);
            setProspect(response.data.data);
        } catch (err) {
            console.error('Failed to load prospect', err);
        } finally {
            setLoading(false);
        }
    }, [orgUuid, prospectUuid]);

    useEffect(() => {
        if (open && prospectUuid) {
            loadProspect();
        }
    }, [open, prospectUuid, loadProspect]);

    const handleMarkLost = async () => {
        if (!prospect) return;
        try {
            await markProspectLost(orgUuid, prospect.uuid, lostReason);
            setLostDialogOpen(false);
            setLostReason('');
            await loadProspect();
            onProspectUpdated();
        } catch (err) {
            console.error('Failed to mark lost', err);
        }
    };

    const handleReactivate = async () => {
        if (!prospect) return;
        try {
            await reactivateProspect(orgUuid, prospect.uuid);
            await loadProspect();
            onProspectUpdated();
        } catch (err) {
            console.error('Failed to reactivate', err);
        }
    };

    const handleStageDetailsSaved = async () => {
        setEditingStage(null);
        setAdvanceOnSave(false);
        await loadProspect();
        onProspectUpdated();
    };

    // Stages that require a details form before advancing
    const STAGES_WITH_FORMS: FunnelStage[] = [
        'discovery',
        'sized',
        'proposal',
        'verbal',
        'closed',
        'launch_install',
    ];

    const handleAdvanceClick = async () => {
        if (!prospect) return;
        const stage = prospect.current_stage;
        if (STAGES_WITH_FORMS.includes(stage)) {
            // Open the details form for this stage with advance-on-save
            setAdvanceOnSave(true);
            setEditingStage(stage);
        } else {
            // Simple stages (identified, qualified): advance directly
            setAdvancing(true);
            try {
                await advanceSalesFunnelProspect(orgUuid, prospect.uuid);
                await loadProspect();
                onProspectUpdated();
            } catch (err) {
                console.error('Failed to advance prospect', err);
            } finally {
                setAdvancing(false);
            }
        }
    };

    const currentStageOrder = prospect ? FUNNEL_STAGE_MAP[prospect.current_stage]?.order ?? 0 : 0;

    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return '--';
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    };

    const formatCurrency = (value: number | null | undefined) => {
        if (value == null) return '--';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
        }).format(value);
    };

    return (
        <>
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                PaperProps={{ style: { width: isMobile ? '100%' : '55%' } }}
            >
                {loading || !prospect ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        {/* Header */}
                        <Box
                            sx={{
                                p: 2,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                            }}
                        >
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        {prospect.prospect_name}
                                    </Typography>
                                    <Chip
                                        label={FUNNEL_STAGE_MAP[prospect.current_stage]?.label}
                                        size="small"
                                        sx={{
                                            backgroundColor:
                                                FUNNEL_STAGE_MAP[prospect.current_stage]?.color,
                                            fontWeight: 600,
                                        }}
                                    />
                                    {prospect.lost_at && (
                                        <Chip label="Lost" size="small" color="error" />
                                    )}
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    {prospect.contact_name && (
                                        <Box
                                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                        >
                                            <PersonIcon
                                                sx={{ fontSize: 16, color: 'text.secondary' }}
                                            />
                                            <Typography variant="body2" color="text.secondary">
                                                {prospect.contact_name}
                                            </Typography>
                                        </Box>
                                    )}
                                    {prospect.contact_email && (
                                        <Box
                                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                        >
                                            <EmailIcon
                                                sx={{ fontSize: 16, color: 'text.secondary' }}
                                            />
                                            <Typography variant="body2" color="text.secondary">
                                                {prospect.contact_email}
                                            </Typography>
                                        </Box>
                                    )}
                                    {prospect.contact_phone && (
                                        <Box
                                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                        >
                                            <PhoneIcon
                                                sx={{ fontSize: 16, color: 'text.secondary' }}
                                            />
                                            <Typography variant="body2" color="text.secondary">
                                                {prospect.contact_phone}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                            <IconButton onClick={onClose} size="small">
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        {/* Stage Progress Stepper */}
                        <Box
                            sx={{ px: 2, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}
                        >
                            <Stepper activeStep={currentStageOrder} alternativeLabel>
                                {FUNNEL_STAGES.map((stage) => (
                                    <Step
                                        key={stage.key}
                                        completed={
                                            prospect[
                                                `${stage.key}_at` as keyof SalesFunnelProspect
                                            ] != null
                                        }
                                    >
                                        <StepLabel>
                                            <Typography
                                                variant="caption"
                                                sx={{ fontSize: '0.6rem' }}
                                            >
                                                {stage.label}
                                            </Typography>
                                        </StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                        </Box>

                        {/* Content - scrollable */}
                        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                            {/* Prospect Info Summary */}
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                    Prospect Info
                                </Typography>
                                <Grid container spacing={1}>
                                    <Grid item xs={6} sm={4}>
                                        <Typography variant="caption" color="text.secondary">
                                            Facility Type
                                        </Typography>
                                        <Typography variant="body2">
                                            {prospect.facility_type || '--'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={4}>
                                        <Typography variant="caption" color="text.secondary">
                                            Facility Size
                                        </Typography>
                                        <Typography variant="body2">
                                            {prospect.facility_size || '--'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={4}>
                                        <Typography variant="caption" color="text.secondary">
                                            Athlete Profile
                                        </Typography>
                                        <Typography variant="body2">
                                            {prospect.athlete_profile || '--'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={4}>
                                        <Typography variant="caption" color="text.secondary">
                                            Athlete Count
                                        </Typography>
                                        <Typography variant="body2">
                                            {prospect.athlete_count ?? '--'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={4}>
                                        <Typography variant="caption" color="text.secondary">
                                            Pipeline Value
                                        </Typography>
                                        <Typography variant="body2">
                                            {formatCurrency(prospect.pipeline_value)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={4}>
                                        <Typography variant="caption" color="text.secondary">
                                            Days in Stage
                                        </Typography>
                                        <Typography variant="body2">
                                            {prospect.days_in_current_stage} days
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>

                            <Divider sx={{ mb: 2 }} />

                            {/* Stage Detail Cards */}
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                Stage Details
                            </Typography>
                            {FUNNEL_STAGES.map((stage) => {
                                const hasTimestamp =
                                    prospect[`${stage.key}_at` as keyof SalesFunnelProspect] !=
                                    null;
                                if (!hasTimestamp && stage.order > currentStageOrder) return null;

                                const stageNote = prospect.stage_details?.find(
                                    (sd) => sd.stage === stage.key,
                                );

                                return (
                                    <Box
                                        key={stage.key}
                                        sx={{
                                            mb: 2,
                                            p: 1.5,
                                            border: '1px solid',
                                            borderColor:
                                                prospect.current_stage === stage.key
                                                    ? stage.color
                                                    : 'divider',
                                            borderRadius: 1,
                                            backgroundColor:
                                                prospect.current_stage === stage.key
                                                    ? `${stage.color}15`
                                                    : 'transparent',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                mb: 0.5,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                }}
                                            >
                                                <Chip
                                                    label={stage.label}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: stage.color,
                                                        fontWeight: 600,
                                                        fontSize: '0.7rem',
                                                    }}
                                                />
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {formatDate(
                                                        prospect[
                                                            `${stage.key}_at` as keyof SalesFunnelProspect
                                                        ] as string | null,
                                                    )}
                                                </Typography>
                                            </Box>
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setAdvanceOnSave(false);
                                                    setEditingStage(stage.key);
                                                }}
                                            >
                                                <EditIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Box>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: 'block', mb: 0.5 }}
                                        >
                                            {stage.description}
                                        </Typography>

                                        {/* Stage-specific data */}
                                        {stage.key === 'discovery' &&
                                            prospect.discovery_details && (
                                                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                                                    <Grid item xs={6}>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            Use Case
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {prospect.discovery_details.use_case ||
                                                                '--'}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            Budget
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {prospect.discovery_details
                                                                .budget_range || '--'}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            Confirmed Athletes
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {prospect.discovery_details
                                                                .athlete_count_confirmed ?? '--'}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            Meeting Date
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {formatDate(
                                                                prospect.discovery_details
                                                                    .meeting_date,
                                                            )}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            )}
                                        {stage.key === 'sized' && prospect.sizing_details && (
                                            <Grid container spacing={1} sx={{ mt: 0.5 }}>
                                                <Grid item xs={4}>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        Indoor Units
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {prospect.sizing_details.indoor_units}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        Outdoor Units
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {prospect.sizing_details.outdoor_units}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        Beacons
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {prospect.sizing_details.beacons}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        Install Scope
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {prospect.sizing_details.install_scope ||
                                                            '--'}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        Estimated Value
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {formatCurrency(
                                                            prospect.sizing_details.estimated_value,
                                                        )}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        )}
                                        {stage.key === 'proposal' && prospect.proposal_details && (
                                            <Grid container spacing={1} sx={{ mt: 0.5 }}>
                                                <Grid item xs={6}>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        Sent
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {formatDate(
                                                            prospect.proposal_details
                                                                .proposal_sent_at,
                                                        )}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        Amount
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {formatCurrency(
                                                            prospect.proposal_details
                                                                .proposal_amount,
                                                        )}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        )}
                                        {(stage.key === 'verbal' || stage.key === 'closed') &&
                                            prospect.close_details && (
                                                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                                                    <Grid item xs={4}>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            LOI Date
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {formatDate(
                                                                prospect.close_details
                                                                    .loi_signed_at,
                                                            )}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            Contract Date
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {formatDate(
                                                                prospect.close_details
                                                                    .contract_signed_at,
                                                            )}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            Contract Value
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {formatCurrency(
                                                                prospect.close_details
                                                                    .contract_value,
                                                            )}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            )}
                                        {stage.key === 'launch_install' &&
                                            prospect.install_details && (
                                                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                                                    <Grid item xs={4}>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            Start Date
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {formatDate(
                                                                prospect.install_details
                                                                    .install_start_date,
                                                            )}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            End Date
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {formatDate(
                                                                prospect.install_details
                                                                    .install_end_date,
                                                            )}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            Status
                                                        </Typography>
                                                        <Chip
                                                            label={
                                                                prospect.install_details
                                                                    .install_status
                                                            }
                                                            size="small"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            Indoor Installed
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {
                                                                prospect.install_details
                                                                    .indoor_units_installed
                                                            }
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            Outdoor Installed
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {
                                                                prospect.install_details
                                                                    .outdoor_units_installed
                                                            }
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            Beacons Installed
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {
                                                                prospect.install_details
                                                                    .beacons_installed
                                                            }
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            )}

                                        {stageNote?.notes && (
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    mt: 1,
                                                    fontStyle: 'italic',
                                                    color: 'text.secondary',
                                                }}
                                            >
                                                {stageNote.notes}
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            })}

                            <Divider sx={{ my: 2 }} />

                            {/* Activity Log */}
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                Activity Log
                            </Typography>
                            {prospect.activity_log && prospect.activity_log.length > 0 ? (
                                prospect.activity_log.map((log) => (
                                    <Box
                                        key={log.uuid}
                                        sx={{
                                            display: 'flex',
                                            gap: 1,
                                            mb: 1,
                                            pb: 1,
                                            borderBottom: '1px solid',
                                            borderColor: 'divider',
                                        }}
                                    >
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2">
                                                {log.description}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {log.user?.name || 'System'} &middot;{' '}
                                                {new Date(log.created_at).toLocaleString()}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No activity yet.
                                </Typography>
                            )}
                        </Box>

                        {/* Action Bar */}
                        <Box
                            sx={{
                                p: 2,
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                display: 'flex',
                                gap: 1,
                                flexWrap: 'wrap',
                            }}
                        >
                            {!prospect.lost_at &&
                                prospect.current_stage !== 'launch_install' &&
                                (() => {
                                    const currentConfig = FUNNEL_STAGE_MAP[prospect.current_stage];
                                    const nextConfig = FUNNEL_STAGES.find(
                                        (s) => s.order === (currentConfig?.order ?? 0) + 1,
                                    );
                                    const needsForm = STAGES_WITH_FORMS.includes(
                                        prospect.current_stage,
                                    );
                                    return (
                                        <Button
                                            variant="contained"
                                            startIcon={<ArrowForwardIcon />}
                                            onClick={handleAdvanceClick}
                                            disabled={advancing}
                                            size="small"
                                        >
                                            {advancing
                                                ? 'Advancing...'
                                                : needsForm
                                                ? `Complete ${currentConfig?.label} & Advance to ${
                                                      nextConfig?.label ?? 'Next'
                                                  }`
                                                : `Advance to ${nextConfig?.label ?? 'Next Stage'}`}
                                        </Button>
                                    );
                                })()}
                            {!prospect.lost_at ? (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<BlockIcon />}
                                    onClick={() => setLostDialogOpen(true)}
                                    size="small"
                                >
                                    Mark Lost
                                </Button>
                            ) : (
                                <Button
                                    variant="outlined"
                                    startIcon={<RestoreIcon />}
                                    onClick={handleReactivate}
                                    size="small"
                                >
                                    Reactivate
                                </Button>
                            )}
                        </Box>
                    </Box>
                )}
            </Drawer>

            {/* Mark Lost Dialog */}
            <Dialog
                open={lostDialogOpen}
                onClose={() => setLostDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Mark Prospect as Lost</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Reason (optional)"
                        value={lostReason}
                        onChange={(e) => setLostReason(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLostDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleMarkLost}>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Stage Details Edit Dialog */}
            {editingStage && prospect && (
                <StageDetailsForm
                    open={!!editingStage}
                    onClose={() => {
                        setEditingStage(null);
                        setAdvanceOnSave(false);
                    }}
                    stage={editingStage}
                    prospect={prospect}
                    orgUuid={orgUuid}
                    onSaved={handleStageDetailsSaved}
                    advanceOnSave={advanceOnSave}
                />
            )}
        </>
    );
};

export default ProspectDetailDrawer;
